import { Node, Edge } from '@xyflow/react';
import {
  type StepInput,
  type ProcessEventNode,
  type ProcessStepRecording,
  type PopulatedUser,
  type System,
  type Artifact,
  type WhoElseDoesThisItem,
  type PopulatedExternalContact
} from '@bliks-io/contracts';
import type { SuccessorUpdateData, SuccessorChangeHandler } from '../types';

/** Extracts all ruleIds from the first previous_output input. */
export function getPreviousOutputRuleIds(inputs?: StepInput[]): string[] | undefined {
  const input = inputs?.find((i) => i.inputType === 'previous_output');
  if (input?.inputType !== 'previous_output') return undefined;
  return input.sourceSteps?.flatMap((s) => s.ruleIds ?? []);
}

/**
 * Resolve the recording's predecessor event node (ADR 0003): the event-node
 * id may sit directly in `previousStep` (publishes after the editor flip) or
 * arrive via the step's stamped event-input `eventId` (backfilled legacy
 * data). When several event inputs resolve, the start event wins — it is the
 * chronological predecessor; intermediates are mid-process attachments.
 * Returns undefined when nothing resolves (orphan-repair rows) — the start
 * card then degrades to the literal previousStep / generic label fallback.
 */
function resolvePredecessorEventNode(
  recording: ProcessStepRecording | null,
  events: ProcessEventNode[] | undefined
): ProcessEventNode | undefined {
  if (!recording || !events || events.length === 0) return undefined;
  const eventsById = new Map(events.map((event) => [event._id, event]));

  if (typeof recording.previousStep === 'string') {
    const byPreviousStep = eventsById.get(recording.previousStep);
    if (byPreviousStep) return byPreviousStep;
  }

  const resolved = (recording.inputs ?? []).flatMap((input) => {
    if (input.inputType !== 'event' || !input.eventId) return [];
    const node = eventsById.get(input.eventId);
    return node ? [node] : [];
  });
  return resolved.find((node) => node.eventKind === 'start') ?? resolved[0];
}

export interface NodeBuilderHandlers {
  handleFieldChange: (field: string, value: unknown) => Promise<void>;
  handleFieldChangeDebounced: (field: string, value: unknown) => void;
  flushPendingFieldChanges: () => Promise<void>;
  handleAddInput: () => void;
  handleEditInput: (index: number) => void;
  handleRemoveInput: (index: number) => void;
  handleOpenDecisionTable: () => void;
  handleSuccessorChange: SuccessorChangeHandler;
  handleWhoElseDoesThisChange: (value: WhoElseDoesThisItem[]) => Promise<void>;
}

export interface NodeBuilderData {
  currentRecording: ProcessStepRecording | null;
  /** Event nodes from the process response — source of the start-node display values. */
  events?: ProcessEventNode[];
  availableSuccessors: {
    persons: PopulatedUser[];
    systems: System[];
    externalContacts: PopulatedExternalContact[];
  };
  artifacts: Artifact[];
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Builds successor values by merging overrides on top of existing values.
 * When overrides is undefined, returns existing values with defaults.
 */
function buildSuccessorValues(
  existing: Partial<SuccessorUpdateData>,
  overrides?: SuccessorUpdateData
): Required<SuccessorUpdateData> {
  const source = overrides ?? {};
  return {
    persons: source.persons ?? existing.persons ?? [],
    orgFunctions: source.orgFunctions ?? existing.orgFunctions ?? [],
    systems: source.systems ?? existing.systems ?? [],
    externalContacts: source.externalContacts ?? existing.externalContacts ?? [],
    successorStatus: source.successorStatus ?? existing.successorStatus ?? ''
  };
}

/**
 * Merges new successor data into an existing rules array.
 * Rules at indices in `editedIndices` get updated values; others keep existing.
 */
function mergeRuleSuccessors(
  rules: Array<{ output?: string; successors?: SuccessorUpdateData; [key: string]: unknown }>,
  editedIndices: number[],
  data: SuccessorUpdateData
) {
  return rules.map((rule, index) => ({
    ...rule,
    successors: buildSuccessorValues(
      rule.successors ?? {},
      editedIndices.includes(index) ? data : undefined
    )
  }));
}

/**
 * Build nodes for the process flow diagram
 * Extracted from ProcessStepRecording to reduce component size
 */
export function buildProcessNodes(data: NodeBuilderData, handlers: NodeBuilderHandlers): Node[] {
  const { currentRecording, events, availableSuccessors, artifacts, t } = data;
  const {
    handleFieldChange,
    handleFieldChangeDebounced,
    flushPendingFieldChanges,
    handleAddInput,
    handleEditInput,
    handleRemoveInput,
    handleOpenDecisionTable,
    handleSuccessorChange,
    handleWhoElseDoesThisChange
  } = handlers;

  // The start node's display values come from the predecessor event node.
  const startEventSource = resolvePredecessorEventNode(currentRecording, events);

  const nodes: Node[] = [
    {
      id: 'inset-s-1',
      type: 'startEvent',
      position: { x: 0, y: 0 }, // Will be calculated by layout
      data: {
        label:
          startEventSource?.name ||
          (typeof currentRecording?.previousStep === 'string'
            ? currentRecording.previousStep
            : t('viralRecording.labels.previousEvent')),
        description: startEventSource?.description,
        eventType: startEventSource?.eventType,
        onCreateStartEvent: undefined, // No edit functionality in recording mode
        predecessorStep: currentRecording?.previousStep, // Pass populated predecessor step
        ruleIds: getPreviousOutputRuleIds(currentRecording?.inputs),
        ruleId: getPreviousOutputRuleIds(currentRecording?.inputs)?.[0]
      }
    },
    {
      id: 'step-with-who-1',
      type: 'stepWithWhoElse',
      position: { x: 0, y: 0 }, // Will be calculated by layout
      data: {
        // Step card data
        recording: currentRecording,
        availableUsers: availableSuccessors.persons || [],
        availableSystems: availableSuccessors.systems || [],
        availableArtifacts: artifacts || [],
        onChange: handleFieldChange,
        onChangeDebounced: handleFieldChangeDebounced,
        onFlushPendingChanges: flushPendingFieldChanges,
        onAddInput: handleAddInput,
        onEditInput: handleEditInput,
        onRemoveInput: handleRemoveInput,
        onOpenDecisionTable: handleOpenDecisionTable,
        // Who else does this data
        whoElseValue: currentRecording?.whoElseDoesThis || [],
        onWhoElseChange: handleWhoElseDoesThisChange
      }
    }
  ];

  // Build successor data array for the successors column
  const successorsData: Array<{
    outputLabel?: string;
    value: Required<SuccessorUpdateData>;
    onChange: (data: SuccessorUpdateData) => void | Promise<void>;
    sourceRuleId?: string;
  }> = [];

  if (
    currentRecording?.actionType === 'decision' &&
    currentRecording.rules &&
    currentRecording.rules.length > 0
  ) {
    // Capture rules reference for use in closures (TypeScript loses narrowing otherwise)
    const rules = currentRecording.rules;

    // Group rules by output to create one successor entry per unique output
    const rulesByOutput = new Map<string, number[]>();
    rules.forEach((rule, index) => {
      const output = rule.output || '';
      const existing = rulesByOutput.get(output);
      if (existing) {
        existing.push(index);
      } else {
        rulesByOutput.set(output, [index]);
      }
    });

    // Create successor data for each unique output
    rulesByOutput.forEach((ruleIndices, outputLabel) => {
      const firstRuleIndex = ruleIndices[0];
      const firstRule = rules[firstRuleIndex];

      successorsData.push({
        outputLabel: outputLabel || undefined,
        value: {
          persons: firstRule.successors?.persons ?? [],
          orgFunctions: firstRule.successors?.orgFunctions ?? [],
          systems: firstRule.successors?.systems ?? [],
          externalContacts: firstRule.successors?.externalContacts ?? [],
          successorStatus: firstRule.successors?.successorStatus ?? ''
        },
        sourceRuleId: (firstRule as { _id?: string })._id,
        onChange: async (data: SuccessorUpdateData) => {
          const updatedRules = mergeRuleSuccessors(rules, ruleIndices, data);
          await handleFieldChange('rules', updatedRules);
        }
      });
    });
  } else {
    // Single successor for task type or when no rules defined
    successorsData.push({
      value: {
        persons: currentRecording?.task?.successors?.persons ?? [],
        orgFunctions: currentRecording?.task?.successors?.orgFunctions ?? [],
        systems: currentRecording?.task?.successors?.systems ?? [],
        externalContacts: currentRecording?.task?.successors?.externalContacts ?? [],
        successorStatus: currentRecording?.task?.successors?.successorStatus ?? ''
      },
      onChange: handleSuccessorChange
    });
  }

  // Add single successors column node - gateway and lines are rendered inside the node
  nodes.push({
    id: 'successors-column',
    type: 'successorsColumn',
    position: { x: 0, y: 0 }, // Will be calculated by layout
    data: {
      users: availableSuccessors.persons || [],
      successors: successorsData,
      submitted: false,
      recordingId: currentRecording?._id,
      processId: currentRecording?.process,
      entitySuggestions: currentRecording?.entitySuggestions
    }
  });

  return nodes;
}

/**
 * Build edges for the process flow diagram
 * Simple edges - gateway and branch lines are rendered inside SuccessorsColumnNode
 */
export function buildProcessEdges(): Edge[] {
  return [
    // Connect start to step
    {
      id: 'e-start-step',
      source: 'inset-s-1',
      sourceHandle: 'source',
      target: 'step-with-who-1',
      targetHandle: 'target',
      type: 'smoothstep',
      animated: false
    },
    // Connect step to successors column (gateway is inside the column node)
    {
      id: 'e-step-successors',
      source: 'step-with-who-1',
      sourceHandle: 'source',
      target: 'successors-column',
      targetHandle: 'target',
      type: 'smoothstep',
      animated: false
    }
  ];
}
